import { MessageSquare } from "lucide-react";

import { VibeChatStore } from "../stores";

import { ChatSessionItem } from "./ChatSessionItem";

import { DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatSession as APIChatSession } from "@/queries/vibe/useRecentVibeChatSessions";

const ChatSessionItemShimmer = () => (
  <div className="flex items-center gap-2.5 px-3 py-2">
    <Skeleton className="size-6 flex-shrink-0 rounded-md" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-2.5 w-2/5" />
    </div>
  </div>
);

interface ChatSessionListProps {
  chatSessions: APIChatSession[];
  isLoading: boolean;
  chatStore: VibeChatStore;
  onChatSelect: (chat: APIChatSession) => void;
  onDeleteChat?: (chatId: string) => void;
  onDeleteAllChats?: () => void;
}

export const ChatSessionList = ({
  chatSessions,
  isLoading,
  chatStore,
  onChatSelect,
  onDeleteChat,
  onDeleteAllChats,
}: ChatSessionListProps) => {
  const isEmpty = chatSessions.length === 0 && !isLoading;

  return (
    <>
      <div className="flex items-center justify-between px-1 py-1.5">
        <DropdownMenuLabel className="p-0 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Recent Chats
        </DropdownMenuLabel>
        {onDeleteAllChats && chatSessions.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onDeleteAllChats();
            }}
            className="h-6 px-2 text-xs text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            Clear all
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-1 py-1">
          <ChatSessionItemShimmer />
          <ChatSessionItemShimmer />
          <ChatSessionItemShimmer />
          <ChatSessionItemShimmer />
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
          <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-gray-100">
            <MessageSquare className="size-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-600">No chats yet</p>
          <p className="mt-0.5 text-xs text-gray-400">Start a conversation to see it here</p>
        </div>
      )}

      {!isLoading &&
        chatSessions.map((chat: APIChatSession) => {
          const isActive = chatStore.currentChatId === chat.id;
          const mobxSession = chatStore.chatSessions.find(s => s.id === chat.id);
          return (
            <ChatSessionItem
              key={chat.id}
              chat={chat}
              isActive={isActive}
              isStreaming={chatStore.isLoadingForChat(chat.id)}
              onSelect={() => onChatSelect(chat)}
              onDelete={onDeleteChat}
              displayName={mobxSession?.title}
            />
          );
        })}
    </>
  );
};
